import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Paper, Typography, Container, CircularProgress, Grid, Button, createTheme, ThemeProvider, IconButton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LanguageIcon from '@mui/icons-material/Language';
import TikTokIcon from '@mui/icons-material/MusicNote'; // Using MusicNote for TikTok since there's no official TikTok icon
import DownloadIcon from '@mui/icons-material/Download';
import Fade from '@mui/material/Fade';
import CustomerForm from './CustomerForm';
import { publicApi } from '../../../services/publicApi';
import { analyticsService } from '../../../services/analyticsService';
import Footer from '../../../components/shared/Footer'; // Import Footer component

/**
 * Local Asset interface for the landing page.
 * Defines the structure for design assets like images and fonts.
 */
interface Asset {
    type: string;
    url: string;
    name: string;
    originalWidth?: number;
    originalHeight?: number;
    responsive?: {
        desktop?: { maxWidth: number };
        tablet?: { maxWidth: number };
        mobile?: { maxWidth: number };
    };
}

/**
 * Local CouponData interface specifically for the public landing page.
 * This interface includes the 'branding' field which is transformed from 'customBranding'
 * by the API's mergeBrandingSettings function. We keep this separate from the shared
 * CouponData interface to:
 * 1. Maintain backward compatibility
 * 2. Clearly separate public-facing transformed data from admin data
 * 3. Avoid potential conflicts with other components' assumptions
 */
interface CouponData {
    _id: string;
    code: string;
    title: string;
    description: string;
    design: {
        canvas: {
            backgroundColor?: string;
            breakpoints: {
                [key: string]: {
                    width: number;
                    height: number;
                };
            };
        };
        elements: any[];
        assets?: Asset[];
    };
    discountType: 'percentage' | 'fixed' | 'freeShipping' | 'none' | 'freeform';
    discountValue: number | string;
    startDate: string;
    endDate: string;
    validFrom: string;
    validUntil: string;
    minPurchaseAmount: number;
    qrCode?: string;
    marketingSettings: any;
    branding?: {
        theme: {
            primaryColor: string;
            secondaryColor: string;
            backgroundColor: string;
            textColor: string;
            fontFamily: string;
        };
        company: {
            logo: {
                url: string;
                width: number;
            };
            name: string;
            websiteUrl: string;
            socialLinks: {
                [key: string]: string;
            };
        };
        landingPage: {
            welcomeHeading: string;
            welcomeMessage: string;
            defaultWelcomeHeading: string;
            defaultWelcomeMessage: string;
            defaultButtonText: string;
            defaultFooterText: string;
        };
    };
}

interface QRCodeData {
    qrCode: string;
    encodedData: string;
    subscriberId: string;
    status: 'new' | 'existing';
    firstName: string;
    lastName: string;
    email: string;
}

const CouponLanding: React.FC = () => {
    const { couponId } = useParams<{ couponId: string }>();
    const [coupon, setCoupon] = useState<CouponData | null>(null);
    const [qrCodeData, setQRCodeData] = useState<QRCodeData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [couponImageUrl, setCouponImageUrl] = useState<string | null>(null);
    const [downloading, setDownloading] = useState(false);
    const [redeeming, setRedeeming] = useState(false);
    const [redeemed, setRedeemed] = useState(false);

    const theme = React.useMemo(() => {
        if (!coupon?.branding?.theme) {
            return createTheme(); // Default MUI theme
        }

        const brandingTheme = coupon.branding.theme;
        return createTheme({
            palette: {
                primary: {
                    main: brandingTheme.primaryColor || '#1976d2',
                },
                secondary: {
                    main: brandingTheme.secondaryColor || '#dc004e',
                },
                background: {
                    default: brandingTheme.backgroundColor || '#ffffff',
                    paper: brandingTheme.backgroundColor || '#ffffff',
                },
                text: {
                    primary: brandingTheme.textColor || '#000000',
                },
            },
            typography: {
                fontFamily: brandingTheme.fontFamily || 'Roboto',
            },
            components: {
                MuiPaper: {
                    styleOverrides: {
                        root: {
                            backgroundColor: brandingTheme.backgroundColor || '#ffffff',
                            color: brandingTheme.textColor || '#000000',
                        },
                    },
                },
            },
        });
    }, [coupon?.branding?.theme]);

    useEffect(() => {
        if (coupon?.title) {
            document.title = `${coupon.title} - qrPON`;
        } else {
            document.title = 'qrPON';
        }
        // Cleanup function to reset title when component unmounts
        return () => {
            document.title = 'qrPON';
        };
    }, [coupon?.title]);

    useEffect(() => {
        const fetchCoupon = async () => {
            if (!couponId) {
                setError('No coupon ID provided');
                setLoading(false);
                return;
            }

            try {
                const response = await publicApi.getCouponDetails(couponId);
                console.log('Coupon data:', response.data.data.coupon);  // Debug log
                const couponData = response.data.data.coupon;
                setCoupon(couponData); // No need to transform _id, use it directly
            } catch (error: any) {
                console.error('Error fetching coupon:', error);
                setError(error.response?.data?.message || 'Failed to load coupon');
            } finally {
                setLoading(false);
            }
        };

        fetchCoupon();
    }, [couponId]);

    const handleFormSubmit = async (data: { firstName: string; lastName: string; email: string }) => {
        try {
            console.log('Submitting form with data:', data);
            setLoading(true);
            setError(null); // Clear any previous errors
            
            const response = await publicApi.registerCustomer(couponId!, {
                firstName: data.firstName.trim(),
                lastName: data.lastName.trim(),
                email: data.email.trim().toLowerCase()
            });
            
            console.log('Registration response:', response.data);
            
            // Store the form data along with the response data
            setQRCodeData({
                ...response.data.data,
                firstName: data.firstName.trim(),
                lastName: data.lastName.trim(),
                email: data.email.trim().toLowerCase()
            });

            analyticsService.trackEvent('coupon', 'register', couponId);

            // Generate image immediately with the form data
            try {
                const imageResponse = await publicApi.generateCouponImage(couponId!, {
                    customerName: `${data.firstName.trim()} ${data.lastName.trim()}`,
                    customerEmail: data.email.trim().toLowerCase(),
                    subscriberId: response.data.data.subscriberId || ''
                });
                
                const blob = new Blob([imageResponse.data], { type: 'image/png' });
                setCouponImageUrl(URL.createObjectURL(blob));
            } catch (imageError: any) {
                console.error('Error generating image:', imageError);
                setError(imageError.response?.data?.message || 'Failed to generate coupon image');
            }
        } catch (error: any) {
            console.error('Error registering customer:', error);
            analyticsService.trackEvent('coupon', 'register_error', error.message);
            setError(error.response?.data?.message || 'Failed to register');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadImage = async () => {
        if (!couponImageUrl) return;

        try {
            setDownloading(true);
            analyticsService.trackEvent('coupon', 'download', couponId);
            const link = document.createElement('a');
            link.href = couponImageUrl;
            link.download = `${coupon?.code || 'coupon'}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading image:', error);
        } finally {
            setDownloading(false);
        }
    };

    const handleRedeemCoupon = async () => {
        if (!qrCodeData) return;

        try {
            setRedeeming(true);
            await publicApi.redeemCoupon(couponId!, {
                email: qrCodeData.email,
                subscriberId: qrCodeData.subscriberId
            });
            analyticsService.trackEvent('coupon', 'redeem', couponId);
            setRedeemed(true);
        } catch (error) {
            console.error('Error redeeming coupon:', error);
        } finally {
            setRedeeming(false);
        }
    };

    const getFormattedDiscount = (coupon: CouponData) => {
        switch (coupon.discountType) {
            case 'percentage':
                return `${coupon.discountValue}% off`;
            case 'fixed':
                return `$${coupon.discountValue} off`;
            case 'freeShipping':
                return 'Free Shipping';
            case 'none':
                return '';
            case 'freeform':
                return coupon.discountValue?.toString() || '';
            default:
                return '';
        }
    };

    const isValidForSignup = coupon && new Date() >= new Date(coupon.startDate) && new Date() <= new Date(coupon.endDate);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (!coupon) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <Typography>Coupon not found</Typography>
            </Box>
        );
    }

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{
                minHeight: '100vh',
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
                py: { xs: 2, md: 4 },
                backgroundColor: coupon?.branding?.theme?.backgroundColor || 'transparent'
            }}>
                <Container maxWidth="lg">
                    <Fade in>
                        <Paper 
                            elevation={0}
                            sx={{ 
                                p: { xs: 2, md: 4 },
                                borderRadius: 3,
                                backgroundColor: 'white',
                                boxShadow: '0 4px 24px rgba(0,0,0,0.1)'
                            }}
                        >
                            {/* Company Logo */}
                            {coupon.branding?.company?.logo?.url && (
                                <Box display="flex" justifyContent="center" mb={4}>
                                    <img
                                        src={coupon.branding.company.logo.url}
                                        alt={coupon.branding.company.name || 'Company Logo'}
                                        style={{
                                            maxWidth: coupon.branding.company.logo.width || 200,
                                            height: 'auto'
                                        }}
                                    />
                                </Box>
                            )}

                            {qrCodeData ? (
                                <Grid container spacing={4}>
                                    <Grid item xs={12} md={7}>
                                        {couponImageUrl ? (
                                            <Box 
                                                component="img"
                                                src={couponImageUrl}
                                                alt="Your personalized coupon"
                                                sx={{ 
                                                    width: {
                                                        xs: '100%',
                                                        md: coupon.design.canvas.breakpoints.desktop.width
                                                    },
                                                    height: {
                                                        xs: 'auto',
                                                        md: coupon.design.canvas.breakpoints.desktop.height
                                                    },
                                                    display: 'block',
                                                    margin: '0 auto',
                                                    borderRadius: 2,
                                                    boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
                                                    objectFit: {
                                                        xs: 'contain',
                                                        md: 'none'
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <Box sx={{ 
                                                display: 'flex', 
                                                justifyContent: 'center', 
                                                alignItems: 'center',
                                                height: 400
                                            }}>
                                                <CircularProgress />
                                            </Box>
                                        )}
                                    </Grid>
                                    <Grid item xs={12} md={5}>
                                        <Box sx={{ 
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            gap: 3
                                        }}>
                                            <Typography variant="h4" gutterBottom>
                                                {coupon.branding?.landingPage?.defaultWelcomeHeading || 'Welcome Back!'}
                                            </Typography>
                                            <Typography variant="body1" color="text.secondary">
                                                {coupon.branding?.landingPage?.defaultWelcomeMessage || 'You have already registered for this coupon. You can download it again below.'}
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                fullWidth
                                                size="large"
                                                onClick={handleDownloadImage}
                                                disabled={downloading}
                                                startIcon={<DownloadIcon />}
                                                sx={{ mb: 2 }}
                                            >
                                                {downloading ? 'Downloading...' : (coupon.branding?.landingPage?.defaultButtonText || 'DOWNLOAD YOUR COUPON')}
                                            </Button>
                                            {/* Test redeem button - removed */}
                                            {coupon.branding?.landingPage?.defaultFooterText && (
                                                <Typography 
                                                    variant="body2" 
                                                    color="text.secondary"
                                                    sx={{ 
                                                        mt: 2,
                                                        textAlign: 'center',
                                                        fontStyle: 'italic'
                                                    }}
                                                >
                                                    {coupon.branding.landingPage.defaultFooterText}
                                                </Typography>
                                            )}

                                            {(coupon.branding?.company?.websiteUrl || coupon.branding?.company?.socialLinks) && (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
                                                    {coupon.branding.company.websiteUrl && (
                                                        <IconButton
                                                            href={coupon.branding.company.websiteUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            color="primary"
                                                            size="small"
                                                        >
                                                            <LanguageIcon />
                                                        </IconButton>
                                                    )}
                                                    {coupon.branding.company.socialLinks?.facebook && (
                                                        <IconButton
                                                            href={coupon.branding.company.socialLinks.facebook}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            color="primary"
                                                            size="small"
                                                        >
                                                            <FacebookIcon />
                                                        </IconButton>
                                                    )}
                                                    {coupon.branding.company.socialLinks?.twitter && (
                                                        <IconButton
                                                            href={coupon.branding.company.socialLinks.twitter}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            color="primary"
                                                            size="small"
                                                        >
                                                            <TwitterIcon />
                                                        </IconButton>
                                                    )}
                                                    {coupon.branding.company.socialLinks?.instagram && (
                                                        <IconButton
                                                            href={coupon.branding.company.socialLinks.instagram}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            color="primary"
                                                            size="small"
                                                        >
                                                            <InstagramIcon />
                                                        </IconButton>
                                                    )}
                                                    {coupon.branding.company.socialLinks?.linkedin && (
                                                        <IconButton
                                                            href={coupon.branding.company.socialLinks.linkedin}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            color="primary"
                                                            size="small"
                                                        >
                                                            <LinkedInIcon />
                                                        </IconButton>
                                                    )}
                                                    {coupon.branding.company.socialLinks?.tiktok && (
                                                        <IconButton
                                                            href={coupon.branding.company.socialLinks.tiktok}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            color="primary"
                                                            size="small"
                                                        >
                                                            <TikTokIcon />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    </Grid>
                                </Grid>
                            ) : (
                                // New visitor view
                                <>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        alignItems: 'center',
                                        mb: 4
                                    }}>
                                        <Typography 
                                            variant="h3" 
                                            component="h1" 
                                            gutterBottom 
                                            align="center"
                                            sx={{
                                                fontWeight: 'bold',
                                                fontSize: { xs: '2rem', md: '2.5rem' },
                                                mb: 1
                                            }}
                                        >
                                            {coupon.title}
                                        </Typography>
                                        
                                        {coupon.description && (
                                            <Typography 
                                                variant="h6" 
                                                align="center" 
                                                sx={{ 
                                                    maxWidth: '600px',
                                                    mb: 3,
                                                    color: 'text.secondary'
                                                }}
                                            >
                                                {coupon.description}
                                            </Typography>
                                        )}
                                        
                                        <Typography 
                                            variant="h4" 
                                            align="center" 
                                            color="primary"
                                            sx={{
                                                fontWeight: 'bold',
                                                fontSize: { xs: '1.75rem', md: '2rem' },
                                                mb: 2,
                                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent'
                                            }}
                                        >
                                            {getFormattedDiscount(coupon)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ 
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 1,
                                        mt: 2,
                                        mb: 3,
                                        '& .MuiTypography-root': {
                                            color: 'text.secondary',
                                            fontSize: '0.875rem',
                                            textAlign: 'center'
                                        }
                                    }}>
                                        {/*<Typography>
                                            Valid for signup: {new Date(coupon.startDate).toLocaleDateString()} - {new Date(coupon.endDate).toLocaleDateString()}
                                        </Typography>
                                        <Typography>
                                            Valid for redemption: {new Date(coupon.validFrom).toLocaleDateString()} - {new Date(coupon.validUntil).toLocaleDateString()}
                                        </Typography>*/}
                                        <Typography>
                                            Prove you're a person!  Please provide your name and email address<br />
                                            so we can generate your custom offer.<br />
                                            Don't worry!  We won't contact you without your permission.
                                        </Typography>
                                    </Box>
                                    {isValidForSignup ? (
                                        <CustomerForm onSubmit={handleFormSubmit} />
                                    ) : (
                                        <Typography variant="body2" color="error">
                                            This coupon is not valid for signup at this time.
                                        </Typography>
                                    )}
                                    {(coupon.branding?.company?.websiteUrl || coupon.branding?.company?.socialLinks) && (
                                        <Box sx={{ 
                                            display: 'flex', 
                                            justifyContent: 'center',
                                            gap: 2,
                                            mt: 4
                                        }}>
                                            {coupon.branding.company.websiteUrl && (
                                                <IconButton
                                                    href={coupon.branding.company.websiteUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    size="small"
                                                    sx={{ 
                                                        color: coupon.branding?.theme?.secondaryColor || 'text.secondary',
                                                        '&:hover': {
                                                            opacity: 0.8
                                                        }
                                                    }}
                                                >
                                                    <LanguageIcon />
                                                </IconButton>
                                            )}
                                            {coupon.branding.company.socialLinks?.facebook && (
                                                <IconButton
                                                    href={coupon.branding.company.socialLinks.facebook}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    size="small"
                                                    sx={{ 
                                                        color: coupon.branding?.theme?.secondaryColor || 'text.secondary',
                                                        '&:hover': {
                                                            opacity: 0.8
                                                        }
                                                    }}
                                                >
                                                    <FacebookIcon />
                                                </IconButton>
                                            )}
                                            {coupon.branding.company.socialLinks?.twitter && (
                                                <IconButton
                                                    href={coupon.branding.company.socialLinks.twitter}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    size="small"
                                                    sx={{ 
                                                        color: coupon.branding?.theme?.secondaryColor || 'text.secondary',
                                                        '&:hover': {
                                                            opacity: 0.8
                                                        }
                                                    }}
                                                >
                                                    <TwitterIcon />
                                                </IconButton>
                                            )}
                                            {coupon.branding.company.socialLinks?.instagram && (
                                                <IconButton
                                                    href={coupon.branding.company.socialLinks.instagram}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    size="small"
                                                    sx={{ 
                                                        color: coupon.branding?.theme?.secondaryColor || 'text.secondary',
                                                        '&:hover': {
                                                            opacity: 0.8
                                                        }
                                                    }}
                                                >
                                                    <InstagramIcon />
                                                </IconButton>
                                            )}
                                            {coupon.branding.company.socialLinks?.linkedin && (
                                                <IconButton
                                                    href={coupon.branding.company.socialLinks.linkedin}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    size="small"
                                                    sx={{ 
                                                        color: coupon.branding?.theme?.secondaryColor || 'text.secondary',
                                                        '&:hover': {
                                                            opacity: 0.8
                                                        }
                                                    }}
                                                >
                                                    <LinkedInIcon />
                                                </IconButton>
                                            )}
                                            {coupon.branding.company.socialLinks?.tiktok && (
                                                <IconButton
                                                    href={coupon.branding.company.socialLinks.tiktok}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    size="small"
                                                    sx={{ 
                                                        color: coupon.branding?.theme?.secondaryColor || 'text.secondary',
                                                        '&:hover': {
                                                            opacity: 0.8
                                                        }
                                                    }}
                                                >
                                                    <TikTokIcon />
                                                </IconButton>
                                            )}
                                        </Box>
                                    )}
                                </>
                            )}
                        </Paper>
                    </Fade>
                </Container>
                <Footer 
                    variant="public" 
                    backgroundColor={coupon?.branding?.theme?.backgroundColor}
                    branding={coupon?.branding}
                />
            </Box>
        </ThemeProvider>
    );
};

export default CouponLanding;
